import React from 'react';
import { Download, Eye, ImageIcon, Loader2 } from 'lucide-react';
import type { BrandTeamOutput } from '../../../../services/brandTeamService';

interface OutputGalleryProps {
  outputs: BrandTeamOutput[];
  totalExpected: number;
  isGenerating: boolean;
  onPreview?: (output: BrandTeamOutput) => void;
  onDownload?: (output: BrandTeamOutput) => void;
}

const qaStyles: Record<string, { bg: string; text: string; label: string }> = {
  pass: { bg: 'bg-green-500/20', text: 'text-green-700', label: 'Pass' },
  pending: { bg: 'bg-brand-purple/20', text: 'text-brand-purple', label: 'Pending' },
  retry: { bg: 'bg-amber-500/20', text: 'text-amber-700', label: 'Retry' },
  reject: { bg: 'bg-red-500/20', text: 'text-red-700', label: 'Reject' },
};

const OutputGallery: React.FC<OutputGalleryProps> = ({ outputs, totalExpected, isGenerating, onPreview, onDownload }) => {
  if (outputs.length === 0 && !isGenerating) return null;

  const placeholders = Math.max(0, totalExpected - outputs.length);

  return (
    <div className="rounded-xl border border-primary-100 bg-white p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-[15px] font-bold text-text-dark">Generated Outputs</h3>
        <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-body-grey">
          {outputs.length} / {totalExpected}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {outputs.map((output) => {
          const qa = qaStyles[output.qaStatus] || qaStyles.pending;
          const category = (output.metadata?.category as string) || '';
          return (
            <div
              key={output.id}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-gray-100 cursor-pointer transition-transform hover:scale-[1.02]"
              onClick={() => onPreview?.(output)}
            >
              <img src={output.outputUrl} alt={category} className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
                <button onClick={(e) => { e.stopPropagation(); onPreview?.(output); }}
                  className="rounded-full bg-white/90 p-2 shadow-lg transition-transform hover:scale-110" title="Preview">
                  <Eye className="h-4 w-4 text-gray-800" />
                </button>
                {onDownload && (
                  <button onClick={(e) => { e.stopPropagation(); onDownload(output); }}
                    className="rounded-full bg-white/90 p-2 shadow-lg transition-transform hover:scale-110" title="Download">
                    <Download className="h-4 w-4 text-gray-800" />
                  </button>
                )}
              </div>
              <span className={`absolute top-2 right-2 rounded-md px-1.5 py-0.5 text-[9px] font-bold backdrop-blur-sm ${qa.bg} ${qa.text}`}>
                {qa.label}
              </span>
              {category && (
                <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2.5 py-2 text-[10px] font-medium text-white">
                  {category}
                </span>
              )}
            </div>
          );
        })}
        {Array.from({ length: placeholders }).map((_, i) => (
          <div key={`placeholder-${i}`} className="flex aspect-[4/3] items-center justify-center rounded-xl bg-gradient-to-br from-primary-50 to-primary-100">
            {isGenerating && i === 0 ? (
              <Loader2 className="h-6 w-6 animate-spin text-brand-purple" />
            ) : (
              <ImageIcon className="h-6 w-6 text-primary-300" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutputGallery;
